/**
 * RolesConfigTab Component
 * 
 * Admin panel tab for configuring role templates' menu_items
 * Allows admins to control which navigation items each role can access
 * 
 * Features:
 * - List all role templates grouped by department
 * - Edit role's menu_items via checkbox tree
 * - Wildcard toggle for "all menus" access
 * - Save changes to backend
 * 
 * Note: Only superadmins can edit roles (enforced by backend)
 * 
 * Created: 2026-01-27
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { useToast } from '../../hooks/useToast';
import { MENU_STRUCTURE } from '../../config/menuStructure';
import useTableSort from '../../hooks/useTableSort';
import { FilterTemplateDropdown, FilterIcon, useFilterTemplate } from '../common/FilteringTemplate';
import MenuGrantTree from './MenuGrantTree';
import menuGrants from '../../utils/menuGrantTree';

const RolesConfigTab = ({ isSuperAdmin }) => {
  const { instance, accounts } = useMsal();
  const { showToast } = useToast();
  
  // State
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [editedMenuItems, setEditedMenuItems] = useState([]);
  const [isWildcard, setIsWildcard] = useState(false);
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [menuGrantQuery, setMenuGrantQuery] = useState('');
  const [treeQuery, setTreeQuery] = useState('');
  const [drift, setDrift] = useState(null);
  const [driftBusy, setDriftBusy] = useState(false);

  const menuIndex = useMemo(() => menuGrants.indexMenu(MENU_STRUCTURE), []);
  
  // Fetch roles and departments
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      const [rolesResponse, deptsResponse] = await Promise.all([
        authenticatedApiCall(instance, accounts, 'GET', '/role-templates'),
        authenticatedApiCall(instance, accounts, 'GET', '/role-templates/departments')
      ]);
      
      if (rolesResponse.data.success) {
        setRoles(rolesResponse.data.data || []);
      }
      if (deptsResponse.data.success) {
        setDepartments(deptsResponse.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
      showToast('Failed to load roles', 'error');
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, showToast]);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const catalogBody = useCallback(
    () => menuGrants.catalogPayload(menuIndex),
    [menuIndex]
  );

  const refreshDrift = useCallback(async () => {
    if (!isSuperAdmin) return;
    try {
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'POST',
        '/role-templates/menu-catalog/review',
        catalogBody()
      );
      if (response.data.success) setDrift(response.data.data);
    } catch (error) {
      const stale = roles
        .filter((role) => menuGrants.unknownMenuIds(role.menu_items, menuIndex).length > 0)
        .map((role) => ({
          roleId: role.id,
          displayName: role.display_name,
          unknown: menuGrants.unknownMenuIds(role.menu_items, menuIndex),
        }));
      setDrift({
        stored: false,
        added: [],
        removed: [],
        stale,
        emailed: false,
      });
    }
  }, [isSuperAdmin, instance, accounts, catalogBody, roles, menuIndex]);

  useEffect(() => {
    if (!isSuperAdmin || loading) return;
    refreshDrift();
  }, [isSuperAdmin, loading, refreshDrift]);

  const handleAckCatalog = async () => {
    try {
      setDriftBusy(true);
      await authenticatedApiCall(
        instance,
        accounts,
        'POST',
        '/role-templates/menu-catalog/ack',
        catalogBody()
      );
      showToast('Menu catalog marked as reviewed. New menus stay off until a role is checked.', 'success');
      await refreshDrift();
    } catch (error) {
      showToast('Could not save the catalog review', 'error');
    } finally {
      setDriftBusy(false);
    }
  };

  const handlePurgeStale = async () => {
    try {
      setDriftBusy(true);
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'POST',
        '/role-templates/menu-catalog/purge-stale',
        catalogBody()
      );
      const removed = response.data?.data?.idsRemoved || 0;
      showToast(removed ? `Removed ${removed} missing menu grant${removed === 1 ? '' : 's'}` : 'No missing grants to remove', 'success');
      await fetchData();
      await refreshDrift();
    } catch (error) {
      showToast('Could not remove missing menu grants', 'error');
    } finally {
      setDriftBusy(false);
    }
  };
  
  // Group roles by department
  const rolesByDepartment = useMemo(() => {
    const grouped = {};
    
    // Add "No Department" group
    grouped['No Department'] = [];
    
    // Initialize groups for each department
    departments.forEach(dept => {
      grouped[dept.name] = [];
    });
    
    // Sort roles into groups
    roles.forEach(role => {
      const dept = departments.find(d => d.id === role.department_id);
      const groupName = dept ? dept.name : 'No Department';
      if (grouped[groupName]) {
        grouped[groupName].push(role);
      }
    });
    
    // Sort roles within each group by hierarchy_level
    Object.keys(grouped).forEach(key => {
      grouped[key].sort((a, b) => (a.hierarchy_level || 0) - (b.hierarchy_level || 0));
    });
    
    return grouped;
  }, [roles, departments]);
  
  // Filter roles based on department filter and search
  const filteredRoles = useMemo(() => {
    // Always exclude superadmin from the list - it should never be editable from frontend
    let result = roles.filter(r => r.id !== 'superadmin');
    
    // Filter by department
    if (filterDepartment !== 'all') {
      const deptId = parseInt(filterDepartment);
      result = result.filter(r => r.department_id === deptId || (!r.department_id && deptId === 0));
    }
    
    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(r => 
        r.display_name.toLowerCase().includes(term) ||
        r.id.toLowerCase().includes(term)
      );
    }

    if (menuGrantQuery.trim()) {
      result = result.filter((role) => menuGrants.roleMatchesMenuQuery(
        role.menu_items,
        menuGrantQuery,
        menuIndex
      ));
    }
    
    // Sort by hierarchy level
    return result.sort((a, b) => (a.hierarchy_level || 0) - (b.hierarchy_level || 0));
  }, [roles, filterDepartment, searchTerm, menuGrantQuery, menuIndex]);

  const getDepartmentName = useCallback((role) => {
    const dept = departments.find(d => d.id === role.department_id);
    return dept ? dept.name : 'No Department';
  }, [departments]);

  const roleColumns = useMemo(() => [
    { key: 'display_name', label: 'Role Name', type: 'text' },
    { key: 'department_display', label: 'Department', type: 'text' },
  ], []);

  const roleSort = useTableSort({ columns: roleColumns });

  const flatRoles = useMemo(() => filteredRoles.map(r => ({
    ...r,
    department_display: getDepartmentName(r),
  })), [filteredRoles, getDepartmentName]);

  const roleFilter = useFilterTemplate({ items: flatRoles, columns: roleColumns });

  const sortedFilteredRoles = useMemo(() => {
    return roleSort.applySorting(roleFilter.filteredItems);
  }, [roleFilter.filteredItems, roleSort]);

  // Handle selecting a role to edit
  const handleEditRole = (role) => {
    setSelectedRole(role);
    
    // Check if wildcard
    const menuItems = role.menu_items || [];
    if (menuItems.includes('*')) {
      setIsWildcard(true);
      setEditedMenuItems([]);
    } else {
      setIsWildcard(false);
      setEditedMenuItems([...menuItems]);
    }
  };
  
  // Handle canceling edit
  const handleCancelEdit = () => {
    setSelectedRole(null);
    setEditedMenuItems([]);
    setIsWildcard(false);
    setTreeQuery('');
  };
  
  // Toggle wildcard access
  const toggleWildcard = () => {
    setIsWildcard(!isWildcard);
    if (!isWildcard) {
      // Switching to wildcard - clear specific items
      setEditedMenuItems([]);
    }
  };
  
  // Save changes
  const handleSave = async () => {
    if (!selectedRole) return;
    
    try {
      setSaving(true);
      
      const newMenuItems = isWildcard ? ['*'] : editedMenuItems;
      
      const response = await authenticatedApiCall(
        instance, accounts, 'PUT',
        `/role-templates/${selectedRole.id}`,
        { menu_items: newMenuItems }
      );
      
      if (response.data.success) {
        showToast(`Updated menu items for ${selectedRole.display_name}`, 'success');
        
        // Update local state
        setRoles(prev => prev.map(r => 
          r.id === selectedRole.id 
            ? { ...r, menu_items: newMenuItems }
            : r
        ));
        
        // Close edit panel
        handleCancelEdit();
      }
    } catch (error) {
      console.error('Error saving role:', error);
      const message = error.response?.data?.message || 'Failed to save changes';
      showToast(message, 'error');
    } finally {
      setSaving(false);
    }
  };
  
  // Styles
  const styles = {
    container: {
      padding: '20px'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
      flexWrap: 'wrap',
      gap: '12px'
    },
    filters: {
      display: 'flex',
      gap: '12px',
      alignItems: 'center',
      flexWrap: 'wrap'
    },
    select: {
      padding: '8px 12px',
      borderRadius: '4px',
      border: '1px solid #ddd',
      fontSize: '14px',
      minWidth: '180px'
    },
    searchInput: {
      padding: '8px 12px',
      borderRadius: '4px',
      border: '1px solid #ddd',
      fontSize: '14px',
      width: '200px'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      backgroundColor: 'white',
      borderRadius: '8px',
      overflow: 'hidden',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    th: {
      padding: '6px 10px',
      textAlign: 'left',
      backgroundColor: '#f8f9fa',
      borderBottom: '2px solid #dee2e6',
      fontWeight: '600',
      fontSize: '12px',
      color: '#495057',
      lineHeight: 1.3
    },
    td: {
      padding: '4px 10px',
      borderBottom: '1px solid #eee',
      fontSize: '13px',
      lineHeight: 1.3
    },
    editButton: {
      padding: '3px 10px',
      backgroundColor: '#007bff',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '13px'
    },
    editPanel: {
      position: 'fixed',
      top: 0,
      right: 0,
      width: '520px',
      height: '100vh',
      backgroundColor: 'white',
      boxShadow: '-4px 0 20px rgba(0,0,0,0.15)',
      zIndex: 1000,
      display: 'flex',
      flexDirection: 'column'
    },
    editPanelHeader: {
      padding: '20px',
      borderBottom: '1px solid #dee2e6',
      backgroundColor: '#f8f9fa'
    },
    editPanelBody: {
      flex: 1,
      overflow: 'auto',
      padding: '20px'
    },
    editPanelFooter: {
      padding: '16px 20px',
      borderTop: '1px solid #dee2e6',
      display: 'flex',
      gap: '12px',
      justifyContent: 'flex-end'
    },
    menuItemRow: {
      display: 'flex',
      alignItems: 'center',
      padding: '8px 0',
      borderBottom: '1px solid #f0f0f0'
    },
    checkbox: {
      marginRight: '10px',
      width: '18px',
      height: '18px',
      cursor: 'pointer'
    },
    wildcardToggle: {
      padding: '12px 16px',
      backgroundColor: '#fff3cd',
      borderRadius: '6px',
      marginBottom: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    badge: {
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: '12px',
      fontSize: '11px',
      fontWeight: '500'
    },
    wildcardBadge: {
      backgroundColor: '#ffc107',
      color: '#856404'
    },
    saveButton: {
      padding: '10px 20px',
      backgroundColor: '#28a745',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '14px'
    },
    cancelButton: {
      padding: '10px 20px',
      backgroundColor: '#6c757d',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '14px'
    },
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.3)',
      zIndex: 999
    },
    infoBox: {
      padding: '12px 16px',
      backgroundColor: '#d1ecf1',
      borderRadius: '6px',
      marginBottom: '16px',
      fontSize: '13px',
      color: '#0c5460'
    },
    sectionTitle: {
      fontSize: '14px',
      fontWeight: '600',
      marginBottom: '12px',
      color: '#495057'
    }
  };
  
  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
        Loading roles...
      </div>
    );
  }
  
  return (
    <div style={styles.container}>
      {/* Header with filters */}
      <div style={styles.header}>
        <h3 style={{ margin: 0, color: '#333' }}>Role Configuration</h3>
        <div style={styles.filters}>
          <select
            value={filterDepartment}
            onChange={(e) => setFilterDepartment(e.target.value)}
            style={styles.select}
          >
            <option value="all">All Departments</option>
            <option value="0">No Department</option>
            {departments.map(dept => (
              <option key={dept.id} value={dept.id}>{dept.name}</option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Search roles..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
          <input
            type="text"
            placeholder="Find a menu grant..."
            value={menuGrantQuery}
            onChange={(e) => setMenuGrantQuery(e.target.value)}
            style={styles.searchInput}
            title="Show roles that grant this menu, including roles with all menus"
          />
        </div>
      </div>
      
      {/* Info message if not superadmin */}
      {menuGrantQuery.trim() && (
        <div style={{ fontSize: '12px', color: '#374151', marginBottom: '8px' }}>
          {sortedFilteredRoles.length} role{sortedFilteredRoles.length === 1 ? '' : 's'} grant a menu matching “{menuGrantQuery.trim()}”, including roles with all menus.
        </div>
      )}

      {drift && ((drift.added || []).length > 0 || (drift.removed || []).length > 0 || (drift.stale || []).length > 0) && (
        <div style={{ ...styles.infoBox, backgroundColor: '#fff7ed', color: '#9a3412', border: '1px solid #fdba74' }}>
          <strong>Menu catalog needs a review.</strong>
          <div style={{ marginTop: '6px' }}>
            New menus are not turned on for roles that have an explicit list. Roles with all menus already see them.
          </div>
          {(drift.added || []).length > 0 && (
            <div style={{ marginTop: '6px' }}>
              New since the last review: {(drift.added || []).slice(0, 12).join(', ')}
              {drift.added.length > 12 ? ` +${drift.added.length - 12}` : ''}
            </div>
          )}
          {(drift.removed || []).length > 0 && (
            <div style={{ marginTop: '6px' }}>
              Removed since the last review: {(drift.removed || []).slice(0, 12).join(', ')}
            </div>
          )}
          {(drift.stale || []).length > 0 && (
            <div style={{ marginTop: '6px' }}>
              Still granted, but the menu is gone:{' '}
              {(drift.stale || []).slice(0, 8).map((row) => `${row.displayName} (${row.unknown.slice(0, 3).join(', ')})`).join('; ')}
              {drift.stale.length > 8 ? ` +${drift.stale.length - 8} roles` : ''}
            </div>
          )}
          {!drift.stored && (
            <div style={{ marginTop: '6px' }}>The review snapshot is not saved yet, so this list is from the current page only.</div>
          )}
          {isSuperAdmin && (
            <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
              {((drift.added || []).length > 0 || (drift.removed || []).length > 0) && (
                <button type="button" onClick={handleAckCatalog} disabled={driftBusy} style={styles.editButton}>
                  Mark catalog reviewed
                </button>
              )}
              {(drift.stale || []).length > 0 && (
                <button type="button" onClick={handlePurgeStale} disabled={driftBusy} style={{ ...styles.editButton, backgroundColor: '#b45309' }}>
                  Remove missing menus from roles
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {!isSuperAdmin && (
        <div style={{ ...styles.infoBox, backgroundColor: '#fff3cd', color: '#856404', marginBottom: '20px' }}>
          <strong>View Only:</strong> Only superadmins can edit role configurations. Contact your administrator for changes.
        </div>
      )}
      
      {/* Roles table */}
      {roleFilter.getActiveFilters().length > 0 && (
        <div style={{ padding: '0.5rem 1rem', marginBottom: '0.5rem', backgroundColor: '#d1fae5', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.8rem' }}>
          <span style={{ color: '#065f46' }}>
            {roleFilter.getActiveFilters().length} active filter(s) — showing {sortedFilteredRoles.length} of {filteredRoles.length}
          </span>
          <button onClick={() => roleFilter.clearAllFilters()} style={{ padding: '2px 10px', fontSize: '0.75rem', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer' }}>Clear All Filters</button>
        </div>
      )}
      <div style={{ overflowY: 'scroll', overflowX: 'auto', height: '540px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
      <table style={{ ...styles.table, borderRadius: 0, boxShadow: 'none', overflow: 'visible' }}>
        <thead>
          <tr>
            {roleColumns.map(col => (
              <th key={col.key} style={{ ...styles.th, position: 'sticky', top: 0, zIndex: 2, whiteSpace: 'nowrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span onClick={() => roleSort.handleSort(col.key)} style={{ cursor: 'pointer', userSelect: 'none', flex: 1 }} title={`Sort by ${col.label}`}>
                    {col.label}
                    {roleSort.sortColumn === col.key && (<span style={{ fontSize: '0.7rem', marginLeft: '3px' }}>{roleSort.sortDirection === 'asc' ? '▲' : '▼'}</span>)}
                  </span>
                  <span onClick={(e) => roleFilter.openFilterAtPosition(e, col.key)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                    <FilterIcon isActive={!!(roleFilter.columnFilters[col.key]?.length > 0 || roleFilter.filterText[col.key])} color="#666" />
                  </span>
                </div>
                {roleFilter.openFilterColumn === col.key && (
                  <FilterTemplateDropdown
                    columnKey={col.key}
                    columnLabel={col.label}
                    columnFilters={roleFilter.columnFilters}
                    filterMode={roleFilter.filterMode}
                    filterText={roleFilter.filterText}
                    filterSearch={roleFilter.filterSearch}
                    filterButtonPositions={roleFilter.filterButtonPositions}
                    setOpenFilterColumn={roleFilter.setOpenFilterColumn}
                    setFilterSearch={roleFilter.setFilterSearch}
                    getUniqueValues={roleFilter.getUniqueValues}
                    handleFilterToggle={roleFilter.handleFilterToggle}
                    selectAllColumnFilter={roleFilter.selectAllColumnFilter}
                    clearColumnFilter={roleFilter.clearColumnFilter}
                    applyTextFilter={roleFilter.applyTextFilter}
                  />
                )}
              </th>
            ))}
            <th style={{ ...styles.th, position: 'sticky', top: 0, zIndex: 2, width: '100px' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {sortedFilteredRoles.length === 0 ? (
            <tr>
              <td colSpan="3" style={{ ...styles.td, textAlign: 'center', color: '#666' }}>
                No roles found
              </td>
            </tr>
          ) : (
            sortedFilteredRoles.map(role => (
              <tr key={role.id} style={{ 
                backgroundColor: selectedRole?.id === role.id ? '#e7f3ff' : 'transparent'
              }}>
                <td style={styles.td}>
                  <strong>{role.display_name}</strong>
                  {role.is_system && (
                    <span style={{ ...styles.badge, backgroundColor: '#dc3545', color: 'white', marginLeft: '8px' }}>
                      System
                    </span>
                  )}
                  {role.menu_items?.includes('*') && (
                    <span style={{ ...styles.badge, ...styles.wildcardBadge, marginLeft: '8px' }}>
                      *
                    </span>
                  )}
                </td>
                <td style={styles.td}>
                  {role.department_display}
                </td>
                <td style={styles.td}>
                  {isSuperAdmin && (
                    <button
                      onClick={() => handleEditRole(role)}
                      style={styles.editButton}
                      disabled={selectedRole !== null}
                    >
                      Edit
                    </button>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      </div>
      
      {/* Edit Panel (Slide-out) */}
      {selectedRole && (
        <>
          <div style={styles.overlay} onClick={handleCancelEdit} />
          <div style={styles.editPanel}>
            <div style={styles.editPanelHeader}>
              <h3 style={{ margin: '0 0 8px 0' }}>Edit Menu Access</h3>
              <div style={{ fontSize: '14px', color: '#666' }}>
                <strong>{selectedRole.display_name}</strong>
                <span style={{ marginLeft: '8px', color: '#999' }}>
                  ({getDepartmentName(selectedRole)})
                </span>
              </div>
            </div>
            
            <div style={styles.editPanelBody}>
              {/* Info about parent-child inheritance */}
              <div style={styles.infoBox}>
                <strong>Note:</strong> A checked parent includes every menu under it. Those children stay checked and cannot be removed on their own. To grant one child only, leave the parent unchecked and check the child. Department and sub-department still hide items this role cannot open.
              </div>
              
              {/* Wildcard toggle */}
              <div style={styles.wildcardToggle}>
                <input
                  type="checkbox"
                  id="wildcard-toggle"
                  checked={isWildcard}
                  onChange={toggleWildcard}
                  style={{ ...styles.checkbox, marginRight: '8px' }}
                />
                <label htmlFor="wildcard-toggle" style={{ cursor: 'pointer', flex: 1 }}>
                  <strong>Grant all menus ("*")</strong>
                  <div style={{ fontSize: '12px', color: '#856404', marginTop: '2px' }}>
                    User sees all menus allowed by their department
                  </div>
                </label>
              </div>
              
              {/* Menu items checkboxes */}
              {!isWildcard && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '8px' }}>
                    <div style={{ ...styles.sectionTitle, marginBottom: 0 }}>Menu Items</div>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>
                      {editedMenuItems.filter((id) => id !== '*').length} stored
                    </span>
                  </div>
                  <input
                    type="text"
                    placeholder="Search menus..."
                    value={treeQuery}
                    onChange={(e) => setTreeQuery(e.target.value)}
                    style={{ ...styles.searchInput, width: '100%', marginBottom: '8px', boxSizing: 'border-box' }}
                  />
                  <MenuGrantTree
                    structure={MENU_STRUCTURE}
                    grants={editedMenuItems}
                    query={treeQuery}
                    onChange={setEditedMenuItems}
                  />
                  {menuGrants.prefixGrantIds(editedMenuItems, menuIndex).length > 0 && (
                    <div style={{ marginTop: '12px', padding: '8px 10px', backgroundColor: '#f8fafc', borderRadius: '6px' }}>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                        Group grants (covers every menu id that starts with this name)
                      </div>
                      {menuGrants.prefixGrantIds(editedMenuItems, menuIndex).map((id) => (
                        <label key={id} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', padding: '2px 0' }}>
                          <input
                            type="checkbox"
                            checked
                            onChange={() => setEditedMenuItems((prev) => prev.filter((item) => item !== id))}
                          />
                          <span>{id}</span>
                        </label>
                      ))}
                    </div>
                  )}
                  {menuGrants.unknownMenuIds(editedMenuItems, menuIndex).length > 0 && (
                    <div style={{ marginTop: '12px', padding: '8px 10px', backgroundColor: '#fff7ed', borderRadius: '6px' }}>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: '#9a3412', marginBottom: '6px' }}>
                        Missing menus still stored on this role
                      </div>
                      {menuGrants.unknownMenuIds(editedMenuItems, menuIndex).map((id) => (
                        <label key={id} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', padding: '2px 0' }}>
                          <input
                            type="checkbox"
                            checked
                            onChange={() => setEditedMenuItems((prev) => prev.filter((item) => item !== id))}
                          />
                          <span>{id}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div style={styles.editPanelFooter}>
              <button
                onClick={handleCancelEdit}
                style={styles.cancelButton}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                style={{
                  ...styles.saveButton,
                  opacity: saving ? 0.7 : 1,
                  cursor: saving ? 'not-allowed' : 'pointer'
                }}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default RolesConfigTab;
